import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    FileText, ChevronRight, ChevronDown, RefreshCw, Download, Loader2, AlertTriangle, Filter, Search,
} from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    SELECT_ALL, itemsKey,
    fetchCIRClients, fetchCIRSubClients, fetchCIRCCList, fetchCIRPOList, fetchCIRReport, fetchCIRInvoiceItems,
    clearCIRSubClients, clearCIRCCList, clearCIRPOList, clearCIRReport, resetClientInvoiceReport,
    selectCIRClientList, selectCIRClientListLoading,
    selectCIRSubClientList, selectCIRSubClientListLoading,
    selectCIRCCList, selectCIRCCListLoading,
    selectCIRPOList, selectCIRPOListLoading,
    selectCIRReport, selectCIRReportLoading, selectCIRReportError,
    selectCIRItems, selectCIRItemsLoading, selectCIRItemsError,
} from '../../slices/ReportsSlice/clientInvoiceReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed';

// Values are spGetClientInvoiceReceivable's @Rbtntype: Pending = receivable left, Received = fully settled.
const REPORT_TYPES = [
    { value: 'BeforePayment', label: 'Pending', hint: 'Invoices with an amount still receivable' },
    { value: 'AfterPayment', label: 'Received', hint: 'Invoices fully received' },
    { value: 'All', label: 'All Records', hint: 'Every invoice' },
];

// Invoice workflow ids that have an item breakdown (legacy: 488 trading, 562 manufacturing).
const ITEM_MOIDS = ['488', '562'];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// One line per invoice (spGetClientInvoiceReceivable); drives the table, the totals row and the Excel export.
// Receivable = Total + Debit Note - Credit Note - Advance - Client Ded - Any Other - all three Received columns.
const COLUMNS = [
    { key: 'ClientCode', header: 'Client' },
    { key: 'SubClientCode', header: 'Sub-Client' },
    { key: 'ClientInvoiceNo', header: 'Invoice No' },
    { key: 'PoNumber', header: 'PO No' },
    { key: 'RANO', header: 'RA No' },
    { key: 'InvoiceDate', header: 'Inv Date' },
    { key: 'InvoiceMakingDate', header: 'Inv Making Date' },
    { key: 'CCCode', header: 'CC Code' },
    { key: 'BasicValue', header: 'Basic', money: true },
    { key: 'TaxAmt', header: 'Tax Amt', money: true },
    { key: 'Total', header: 'Total', money: true },
    { key: 'Retention', header: 'Retention', money: true },
    { key: 'Advance', header: 'Advance', money: true },
    { key: 'Hold', header: 'Hold', money: true },
    { key: 'ClientDed', header: 'Client Ded', money: true },
    { key: 'AnyOther', header: 'Any Other', money: true },
    { key: 'CreditNote', header: 'Credit Note', money: true },
    { key: 'DebitNote', header: 'Debit Note', money: true },
    { key: 'Received', header: 'Received', money: true },
    { key: 'RetentionReceived', header: 'Retention Received', money: true },
    { key: 'HoldReceived', header: 'Hold Received', money: true },
    { key: 'Receivable', header: 'Receivable', money: true, strong: true },
];
const MONEY_COLUMNS = COLUMNS.filter((c) => c.money);

// Receivable stands out: amber while something is due, green when settled.
const cellTone = (c, row) => {
    if (c.key === 'Receivable') return num(row.Receivable) > 0 ? 'font-bold text-amber-700 dark:text-amber-400' : 'font-bold text-green-700 dark:text-green-400';
    return '';
};

const Label = ({ children }) => (
    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{children}</label>
);

// Item breakdown shown under an expanded trading (488) / manufacturing (562) invoice row.
const InvoiceItems = ({ moid, items, loading, error }) => {
    if (loading) {
        return <div className="flex items-center gap-2 py-3 px-4 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading items…</div>;
    }
    if (error) return <div className="py-3 px-4 text-red-500">{error}</div>;
    if (!items || items.length === 0) return <div className="py-3 px-4 text-gray-400">No items found.</div>;

    const th = 'px-2 py-1 text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase whitespace-nowrap';
    const td = 'px-2 py-1 text-[11px] whitespace-nowrap';
    const sum = (k) => items.reduce((a, i) => a + num(i[k]), 0);

    if (moid === '488') {
        return (
            <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
                <thead className="bg-indigo-50 dark:bg-indigo-900/20">
                    <tr>
                        {['Item Code', 'Item Name', 'Specification', 'UOM'].map((h) => <th key={h} className={`${th} text-left`}>{h}</th>)}
                        {['Basic Price', 'Quantity', 'Amount', 'CGST%', 'CGST Amt', 'SGST%', 'SGST Amt', 'IGST%', 'IGST Amt', 'Total Amount'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {items.map((i, idx) => (
                        <tr key={idx}>
                            <td className={td}>{i.Itemcode}</td>
                            <td className={td}>{i.ItemName}</td>
                            <td className={`${td} whitespace-normal min-w-[180px]`}>{i.Specification}</td>
                            <td className={td}>{i.Units}</td>
                            <td className={`${td} text-right`}>{money(i.BasicPrice)}</td>
                            <td className={`${td} text-right`}>{i.Quantity}</td>
                            <td className={`${td} text-right`}>{money(i.Amount)}</td>
                            <td className={`${td} text-right`}>{i.CGSTPercent}</td>
                            <td className={`${td} text-right`}>{money(i.CGSTAmount)}</td>
                            <td className={`${td} text-right`}>{i.SGSTPercent}</td>
                            <td className={`${td} text-right`}>{money(i.SGSTAmount)}</td>
                            <td className={`${td} text-right`}>{i.IGSTPercent}</td>
                            <td className={`${td} text-right`}>{money(i.IGSTAmount)}</td>
                            <td className={`${td} text-right font-semibold`}>{money(i.TotalAmount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="bg-gray-50 dark:bg-gray-900/40 font-bold">
                        <td className={td} colSpan={6}>TOTAL</td>
                        <td className={`${td} text-right`}>{money(sum('Amount'))}</td>
                        <td className={td} />
                        <td className={`${td} text-right`}>{money(sum('CGSTAmount'))}</td>
                        <td className={td} />
                        <td className={`${td} text-right`}>{money(sum('SGSTAmount'))}</td>
                        <td className={td} />
                        <td className={`${td} text-right`}>{money(sum('IGSTAmount'))}</td>
                        <td className={`${td} text-right`}>{money(sum('TotalAmount'))}</td>
                    </tr>
                </tfoot>
            </table>
        );
    }

    // Manufacturing invoice: manufactured items carry price/amount, stock items only quantity (as legacy)
    return (
        <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded">
            <thead className="bg-indigo-50 dark:bg-indigo-900/20">
                <tr>
                    {['Type', 'Item Code', 'Item Name', 'Specification', 'UOM'].map((h) => <th key={h} className={`${th} text-left`}>{h}</th>)}
                    {['Quantity', 'Basic Price', 'Amount'].map((h) => <th key={h} className={`${th} text-right`}>{h}</th>)}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {items.map((i, idx) => {
                    const isMfg = i.ItemType === 'Manufacturing';
                    return (
                        <tr key={idx} className={isMfg ? '' : 'bg-amber-50 dark:bg-amber-900/10'}>
                            <td className={`${td} font-semibold`}>{isMfg ? 'MANUFACTURING' : 'STOCK'}</td>
                            <td className={td}>{i.Itemcode}</td>
                            <td className={td}>{i.ItemName}</td>
                            <td className={`${td} whitespace-normal min-w-[180px]`}>{i.Specification}</td>
                            <td className={td}>{i.Units}</td>
                            <td className={`${td} text-right`}>{i.Quantity}</td>
                            <td className={`${td} text-right`}>{isMfg ? money(i.BasicPrice) : ''}</td>
                            <td className={`${td} text-right`}>{isMfg ? money(i.Amount) : ''}</td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-900/40 font-bold">
                    <td className={td} colSpan={7}>TOTAL</td>
                    <td className={`${td} text-right`}>{money(sum('Amount'))}</td>
                </tr>
            </tfoot>
        </table>
    );
};

const ClientInvoiceReport = () => {
    const dispatch = useDispatch();

    const clientList = useSelector(selectCIRClientList);
    const clientListLoading = useSelector(selectCIRClientListLoading);
    const subClientList = useSelector(selectCIRSubClientList);
    const subClientListLoading = useSelector(selectCIRSubClientListLoading);
    const ccList = useSelector(selectCIRCCList);
    const ccListLoading = useSelector(selectCIRCCListLoading);
    const poList = useSelector(selectCIRPOList);
    const poListLoading = useSelector(selectCIRPOListLoading);
    const report = useSelector(selectCIRReport);
    const reportLoading = useSelector(selectCIRReportLoading);
    const reportError = useSelector(selectCIRReportError);
    const items = useSelector(selectCIRItems);
    const itemsLoading = useSelector(selectCIRItemsLoading);
    const itemsError = useSelector(selectCIRItemsError);

    // Filters live only in local state (no Redux sync effect, see the report-page filter sync bug).
    const [reportType, setReportType] = useState('');
    const [clientCode, setClientCode] = useState('');
    const [subClientCode, setSubClientCode] = useState('');
    const [ccStatus, setCcStatus] = useState('');
    const [ccCode, setCcCode] = useState('');
    const [poNo, setPoNo] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [ranParams, setRanParams] = useState(null);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        dispatch(fetchCIRClients());
        return () => dispatch(resetClientInvoiceReport());
    }, [dispatch]);

    // GetAllclients already returns its own "Select All" row; the other lists get one added here,
    // as legacy does (CC only when the list is not empty).
    const subClientOptions = useMemo(() => (
        subClientList.length ? [{ SubClientCode: SELECT_ALL, SubClientCodename: 'Select All' }, ...subClientList] : []
    ), [subClientList]);
    const ccOptions = useMemo(() => (
        ccList.length ? [{ CC_Code: SELECT_ALL, CC_Name: 'Select All' }, ...ccList] : []
    ), [ccList]);
    const poOptions = useMemo(() => (
        poList.length ? [{ ClientPOId: SELECT_ALL, PONumber: SELECT_ALL }, ...poList] : []
    ), [poList]);

    const clearResults = () => { setRanParams(null); setExpanded(null); dispatch(clearCIRReport()); };

    const handleClientChange = (val) => {
        setClientCode(val);
        setSubClientCode(''); setCcStatus(''); setCcCode(''); setPoNo('');
        dispatch(clearCIRSubClients()); dispatch(clearCIRCCList()); dispatch(clearCIRPOList());
        clearResults();
        if (val) dispatch(fetchCIRSubClients(val));
    };

    const handleSubClientChange = (val) => {
        setSubClientCode(val);
        // legacy resets CC status here; the CC/PO lists depend on it, so clear them too
        setCcStatus(''); setCcCode(''); setPoNo('');
        dispatch(clearCIRCCList()); dispatch(clearCIRPOList());
        clearResults();
    };

    const handleCCStatusChange = (val) => {
        setCcStatus(val);
        setCcCode(''); setPoNo('');
        dispatch(clearCIRCCList()); dispatch(clearCIRPOList());
        clearResults();
        if (val && clientCode && subClientCode) dispatch(fetchCIRCCList({ clientCode, subClientCode, ccStatus: val }));
    };

    const handleCCChange = (val) => {
        setCcCode(val);
        setPoNo('');
        dispatch(clearCIRPOList());
        clearResults();
        if (val) dispatch(fetchCIRPOList({ clientCode, subClientCode, ccCode: val }));
    };

    const handleReset = () => {
        setReportType(''); setClientCode(''); setSubClientCode(''); setCcStatus(''); setCcCode(''); setPoNo('');
        setFromDate(''); setToDate(''); setRanParams(null); setExpanded(null);
        dispatch(clearCIRSubClients()); dispatch(clearCIRCCList()); dispatch(clearCIRPOList()); dispatch(clearCIRReport());
    };

    const runReport = () => {
        const missing = [];
        if (!reportType) missing.push('Report Type');
        if (!clientCode) missing.push('Client');
        if (!subClientCode) missing.push('Sub Client');
        if (!ccStatus) missing.push('Cost Centre Status');
        if (!ccCode) missing.push('Cost Centre');
        if (!poNo) missing.push('PO No');
        if (missing.length) { toast.warning(`Please select: ${missing.join(', ')}`); return; }

        // PO is sent by its number (display text), not its id - same as legacy.
        const params = {
            Client: clientCode,
            SubClient: subClientCode,
            From: toDDMonYYYY(fromDate),
            To: toDDMonYYYY(toDate),
            CCCode: ccCode,
            PO: poNo,
            Type: reportType,
            CCStatusval: ccStatus,
        };
        setRanParams(params);
        setExpanded(null);
        dispatch(fetchCIRReport(params));
    };

    const handleRefresh = () => { if (ranParams) { setExpanded(null); dispatch(fetchCIRReport(ranParams)); } };

    const toggleRow = (row, idx) => {
        if (expanded === idx) { setExpanded(null); return; }
        setExpanded(idx);
        const k = itemsKey(row.ClientInvoiceNo, row.PoNumber);
        if (!items[k] && !itemsLoading[k]) {
            dispatch(fetchCIRInvoiceItems({ invoiceNo: row.ClientInvoiceNo, poNo: row.PoNumber, moid: String(row.MOID) }));
        }
    };

    // One line per invoice, so every amount column can be totalled.
    const totals = useMemo(() => MONEY_COLUMNS.reduce((acc, c) => {
        acc[c.key] = report.reduce((a, r) => a + num(r[c.key]), 0);
        return acc;
    }, {}), [report]);
    const pendingCount = useMemo(() => report.filter((r) => num(r.Receivable) > 0).length, [report]);

    const handleExport = () => {
        if (report.length === 0) { toast.warning('No data to export'); return; }
        const rows = report.map((r) => {
            const row = {};
            COLUMNS.forEach((c) => { row[c.header] = c.money ? num(r[c.key]) : r[c.key]; });
            return row;
        });
        const totalRow = {};
        COLUMNS.forEach((c, i) => { totalRow[c.header] = c.money ? Math.round(totals[c.key] * 100) / 100 : (i === 0 ? 'TOTAL' : ''); });
        rows.push(totalRow);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Client Invoices');
        XLSX.writeFile(wb, `Client_Invoice_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    const typeLabel = REPORT_TYPES.find((t) => t.value === ranParams?.Type)?.label;

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <FileText className="h-4 w-4 text-white" />
                            </div>
                            Client Invoice Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">One line per client invoice: deductions, credit/debit notes, receipts and the amount still receivable</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Client Invoice Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={report.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>

                <div className="flex items-center gap-4 mb-3">
                    <Label>Report Type</Label>
                    {REPORT_TYPES.map((t) => (
                        <label key={t.value} title={t.hint} className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200 cursor-pointer -mt-1">
                            <input type="radio" name="cirReportType" className="accent-indigo-600" value={t.value}
                                checked={reportType === t.value} onChange={() => { setReportType(t.value); clearResults(); }} />
                            {t.label}
                        </label>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <Label>Client</Label>
                        <select className={inputCls} value={clientCode} onChange={(e) => handleClientChange(e.target.value)} disabled={clientListLoading}>
                            <option value="">{clientListLoading ? 'Loading…' : '-Please Select-'}</option>
                            {clientList.map((c) => <option key={c.Clientcode} value={c.Clientcode}>{c.clientname}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>Sub-Client</Label>
                        <select className={inputCls} value={subClientCode} onChange={(e) => handleSubClientChange(e.target.value)} disabled={!clientCode || subClientListLoading}>
                            <option value="">{subClientListLoading ? 'Loading…' : '-Please Select-'}</option>
                            {subClientOptions.map((c) => <option key={c.SubClientCode} value={c.SubClientCode}>{c.SubClientCodename}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>Cost Centre Status</Label>
                        <select className={inputCls} value={ccStatus} onChange={(e) => handleCCStatusChange(e.target.value)} disabled={!subClientCode}>
                            <option value="">-Please Select-</option>
                            <option value="Active">Active</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <div>
                        <Label>Cost Centre</Label>
                        <select className={inputCls} value={ccCode} onChange={(e) => handleCCChange(e.target.value)} disabled={!ccStatus || ccListLoading}>
                            <option value="">{ccListLoading ? 'Loading…' : ccStatus && ccOptions.length === 0 ? 'No cost centres' : '-Please Select-'}</option>
                            {ccOptions.map((c) => <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Name}</option>)}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mt-3">
                    <div>
                        <Label>PO No</Label>
                        <select className={inputCls} value={poNo} onChange={(e) => { setPoNo(e.target.value); clearResults(); }} disabled={!ccCode || poListLoading}>
                            <option value="">{poListLoading ? 'Loading…' : ccCode && poOptions.length === 0 ? 'No POs' : '-Please Select-'}</option>
                            {poOptions.map((p) => <option key={p.ClientPOId} value={p.PONumber}>{p.PONumber}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>From Date</Label>
                        <CustomDatePicker value={fromDate} onChange={setFromDate} maxDate={new Date()} />
                    </div>
                    <div>
                        <Label>To Date</Label>
                        <CustomDatePicker value={toDate} onChange={setToDate} minDate={fromDate || null} maxDate={new Date()} />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && report.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : report.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-wrap gap-2">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">{typeLabel} Client Invoices</h2>
                        <div className="flex items-center gap-4 text-[11px] text-gray-500 dark:text-gray-400">
                            <span>Invoices: <b className="text-gray-800 dark:text-gray-100">{report.length}</b></span>
                            <span>With receivable: <b className="text-gray-800 dark:text-gray-100">{pendingCount}</b></span>
                            <span>Total receivable: <b className="text-indigo-700 dark:text-indigo-300">{money(totals.Receivable)}</b></span>
                        </div>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="w-6" />
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} className={`px-2 py-1.5 text-[10px] font-bold text-white uppercase whitespace-nowrap ${c.money ? 'text-right' : 'text-left'}`}>{c.header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {report.map((row, idx) => {
                                    const hasItems = ITEM_MOIDS.includes(String(row.MOID));
                                    const isOpen = expanded === idx;
                                    const k = itemsKey(row.ClientInvoiceNo, row.PoNumber);
                                    return (
                                        <React.Fragment key={idx}>
                                            <tr className={`hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors ${isOpen ? 'bg-indigo-50 dark:bg-indigo-900/10' : ''}`}>
                                                <td className="px-1 text-center">
                                                    {hasItems && (
                                                        <button onClick={() => toggleRow(row, idx)} title="Show items" className="text-indigo-600 hover:text-indigo-800">
                                                            {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                        </button>
                                                    )}
                                                </td>
                                                {COLUMNS.map((c) => (
                                                    <td key={c.key} className={`px-2 py-1 text-xs whitespace-nowrap ${c.money ? 'text-right' : ''} ${cellTone(c, row)}`}>
                                                        {c.money ? money(row[c.key]) : row[c.key]}
                                                    </td>
                                                ))}
                                            </tr>
                                            {isOpen && (
                                                <tr>
                                                    <td colSpan={COLUMNS.length + 1} className="bg-gray-50 dark:bg-gray-900/30 px-4 py-2">
                                                        <InvoiceItems moid={String(row.MOID)} items={items[k]} loading={itemsLoading[k]} error={itemsError[k]} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot className="sticky bottom-0 z-10">
                                <tr className="bg-gray-100 dark:bg-gray-900 font-bold border-t-2 border-gray-300 dark:border-gray-600">
                                    <td />
                                    {COLUMNS.map((c, i) => (
                                        <td key={c.key} className={`px-2 py-1.5 text-xs whitespace-nowrap ${c.money ? 'text-right' : ''} ${c.strong ? 'text-indigo-700 dark:text-indigo-300' : ''}`}>
                                            {c.money ? money(totals[c.key]) : (i === 0 ? `TOTAL (${report.length} invoices)` : '')}
                                        </td>
                                    ))}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientInvoiceReport;
